import { useEffect, useMemo, useState } from "react";
import { look } from "@bd/art";
import { CloseupCanvas, type Actor } from "../world/CloseupCanvas";
import { dateOf, money, type Finale as FinaleData } from "@bd/sim";
import { Icon, Sheet, Tabs } from "@bd/ui-kit";
import { host, me, txLine, useGame } from "../store";
import { currentLang, t, tp } from "../i18n";

interface HallEntry {
  company: string;
  finished: string;
  worth: number;
  rank: number;
  of: number;
  titles: string[];
}

/** The Hall of Fame lives on this device; online seasons will keep it for everyone. */
export function hallOfFame(): HallEntry[] {
  try {
    return JSON.parse(localStorage.getItem("bd:hall") ?? "[]") as HallEntry[];
  } catch {
    return [];
  }
}

export function finaleSeen(seed: string): boolean {
  try {
    return localStorage.getItem(`bd:finaleSeen:${seed}`) === "1";
  } catch {
    return false;
  }
}

function saveToHall(e: HallEntry) {
  try {
    const all = hallOfFame().filter((x) => !(x.company === e.company && x.finished === e.finished));
    localStorage.setItem("bd:hall", JSON.stringify([e, ...all].slice(0, 30)));
  } catch {
    // storage blocked: the finale still plays, it just isn't remembered
  }
}

/** The 2035 awards show: titles revealed one at a time, the final ranking, your company's story, then the Hall of Fame. */
export function Finale() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const co = me(game)!;
  const f = game.finale as FinaleData | undefined;
  const [shown, setShown] = useState(0);
  const [tab, setTab] = useState<"awards" | "standings" | "story" | "hall">("awards");
  // the race titles are revealed one by one; personal records come after, all at once
  const main = (f?.awards ?? []).filter((a) => a.contested !== false);
  useEffect(() => {
    if (!f) return;
    if (shown >= main.length) return;
    const timer = setTimeout(() => setShown((n) => n + 1), shown === 0 ? 600 : 1100);
    return () => clearTimeout(timer);
  }, [shown, f, main.length]);
  useEffect(() => {
    if (!f) return;
    const rank = f.ranking.findIndex((r) => r.id === co.id) + 1;
    try {
      localStorage.setItem(`bd:finaleSeen:${game.seed}`, "1");
    } catch {
      // storage blocked: it may open again next time
    }
    saveToHall({ company: co.name, finished: dateOf(f.week).label, worth: f.ranking.find((r) => r.id === co.id)?.worth ?? 0, rank, of: f.ranking.length, titles: f.awards.filter((a) => a.winner === co.id).map((a) => a.title) });
  }, [f, co, game.seed]);
  const quality = useGame((s) => s.quality);
  // on stage: whoever just won walks up to the podium, and the hall cheers when it's you
  const current = main[Math.max(0, Math.min(shown, main.length) - 1)];
  const actors = useMemo<Actor[]>(() => {
    const out: Actor[] = [];
    const cheer = !!current?.yours;
    for (let i = 0; i < 14; i++) out.push({ id: `seat${i}`, look: look((i * 11 + 4) % 64), seat: i, armsUp: cheer && i % 2 === 0, lean: 0, mouth: "smile" });
    if (current) {
      let h = 0;
      for (const ch of current.winner) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
      out.push({ id: `winner-${current.id}`, look: current.yours ? look(co.staff[0]?.look ?? 3) : look(h % 64), seat: -1, armsUp: true, lean: 0, mouth: "grin" });
    }
    return out;
  }, [current?.id, co.staff]);
  if (!f) return null;
  const story = storyByDecade([...(game.milestones ?? []), ...game.log].filter((e) => e.company === co.id));
  const hall = hallOfFame();
  const revealed = shown >= main.length;
  const records = f.awards.filter((a) => a.contested === false);
  const awards = (
    <div className="grid2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }} data-testid="finale-awards">
      {main.slice(0, shown).map((a) => {
        const others = (a.nominees ?? []).filter((n) => n.id !== a.winner);
        return (
          <div key={a.id} className="card stack" title={[t(a.why), ...others.map((n) => `${n.name}: ${awardValue(n.value)}`)].join("\n")} style={{ padding: "10px 14px", gap: 4, borderColor: a.yours ? "var(--coral)" : undefined, animation: "panel-in 260ms ease-out" }}>
            <span className="row" style={{ gap: 8 }}>
              <Icon name="trophy" color={a.yours ? "var(--coral)" : "var(--gold)"} />
              <strong className="display" style={{ fontSize: "1.15rem" }}>{t(a.title)}</strong>
            </span>
            <span><strong>{a.winnerName}</strong>{a.yours ? ` ${t("(you!)")}` : ""} · {awardValue(a.value)}</span>
            {others.length > 0 && (
              <span className="muted ellipsis" style={{ fontSize: "0.78rem" }} data-testid="finale-nominees">
                {t("vs")} {others.map((n, i) => <span key={n.id} style={{ fontWeight: n.yours ? 700 : undefined, color: n.yours ? "var(--coral)" : undefined }}>{i ? " · " : ""}{n.name} {awardValue(n.value)}</span>)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
  return (
    <>
    <CloseupCanvas kind="finale" year={2035} actors={actors} quality={quality} mood="dusk" />
    <Sheet drawer title={t("The 2035 awards")} onClose={() => set({ screen: null })} wide testId="finale">
      {!revealed && (
        <>
          {awards}
          <button className="btn small ghost" style={{ marginTop: 10 }} onClick={() => setShown(main.length)}><Icon name="fff" size={14} />{t("Show all")}</button>
        </>
      )}
      {revealed && (
        <>
          <Tabs tabs={[{ id: "awards", label: t("Awards") }, { id: "standings", label: t("Standings") }, { id: "story", label: t("Your story") }, { id: "hall", label: t("Hall of Fame") }]} value={tab} onChange={setTab} />
          <div style={{ height: 12 }} />
          {tab === "awards" && (
            <>
              {awards}
              {records.length > 0 && (
                <div className="card stack" style={{ marginTop: 12, gap: 4 }} data-testid="finale-records">
                  <span className="caps">{t("Your records")}</span>
                  <div className="grid2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "2px 16px" }}>
                    {records.map((a) => (
                      <span key={a.id} style={{ fontSize: "0.85rem" }} title={t(a.why)}><strong>{t(a.title)}</strong> <span className="muted">{awardValue(a.value)}</span></span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {tab === "standings" && (
            <div className="card stack" style={{ maxWidth: 520 }} data-testid="finale-standings">
              {f.ranking.map((r, i) => (
                <span key={r.id} className="spread" style={{ fontWeight: r.id === co.id ? 700 : undefined, color: r.id === co.id ? "var(--coral)" : undefined }}>
                  <span>{i + 1}. {r.name}</span>
                  <span className="num" title={r.company === false ? t("~ rival, estimated worth") : undefined}>{r.company === false ? `~${money(r.worth)}` : money(r.worth)}</span>
                </span>
              ))}
            </div>
          )}
          {tab === "story" && (
            <div className="grid2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }} data-testid="finale-story">
              {story.map((d) => (
                <div key={d.decade} className="stack" style={{ gap: 2, borderLeft: "3px solid var(--line)", paddingLeft: 8 }}>
                  <strong style={{ fontSize: "0.85rem" }}>{t("The {decade}s", { decade: d.decade })}</strong>
                  {d.items.map((e, i) => (
                    <span key={i} style={{ fontSize: "0.82rem" }}><span className="muted">{dateOf(e.week).year}</span> · {txLine(storyLine(e.text, co.name))}</span>
                  ))}
                </div>
              ))}
            </div>
          )}
          {tab === "hall" && (
            <div className="card stack" style={{ maxWidth: 520 }}>
              {hall.slice(0, 8).map((h, i) => (
                <span key={i} className="spread" style={{ fontSize: "0.85rem", gap: 8 }}>
                  <span><strong>{h.company}</strong> · {t("#{rank} of {of}", { rank: h.rank, of: h.of })} · {money(h.worth)}</span>
                  {h.titles.length > 0 && <span className="row" style={{ gap: 4 }} title={h.titles.map((x) => t(x)).join(", ")}><Icon name="trophy" size={14} color="var(--gold)" />{h.titles.length}</span>}
                </span>
              ))}
              <button className="btn small primary" style={{ marginTop: 8 }} onClick={() => {
                // a fresh 1965 that keeps what your notebook learned: same name, harder start
                host.newGame(`plus-${Date.now().toString(36)}`, co.name, "you", { notebook: co.notebook });
                useGame.setState({ onboarding: 4, screen: null });
              }} title={t("1965 again as {company}, notebook kept.", { company: co.name })} data-testid="finale-ngplus"><Icon name="replay" size={16} />{t("New Game+")}</button>
            </div>
          )}
        </>
      )}
    </Sheet>
    </>
  );
}

type LogLine = { week: number; kind: string; text: string };

/** Your seventy years, a few moments a decade: the first game, moves, real hits (8+), awards and big deals. */
function storyByDecade(log: LogLine[]): { decade: number; items: LogLine[] }[] {
  const score = (t: string) => Number(/average (\d+\.\d)/.exec(t)?.[1] ?? 0);
  let firstRelease = true;
  const moments = log.filter((e) => {
    if (e.kind === "release") {
      const keep = firstRelease || score(e.text) >= 8;
      firstRelease = false;
      return keep;
    }
    return ["move", "acquire", "store", "direction", "award", "bankrupt"].includes(e.kind);
  });
  const out = new Map<number, LogLine[]>();
  for (const e of moments) {
    const decade = Math.floor(dateOf(e.week).year / 10) * 10;
    const list = out.get(decade) ?? [];
    if (list.length < 3) list.push(e);
    out.set(decade, list);
  }
  return [...out.entries()].map(([decade, items]) => ({ decade, items }));
}

/** A story line without the parts the player already knows: your company's name and the "(mechanic × theme)" aside. */
function storyLine(text: string, company: string): string {
  const s = text.startsWith(`${company} `) ? text.slice(company.length + 1) : text;
  return s.replace(/ \([^)]*×[^)]*\)/, "").replace(/: reviews average (\d+\.\d)\.?$/, " · $1");
}

/** An award's figure ("11 years", "42% hits", "after 2 bankruptcies") in the player's language; English as the engine wrote it. */
function awardValue(v: string): string {
  if (currentLang() === "en") return v;
  const years = /^(\d+) years$/.exec(v);
  if (years) return tp(Number(years[1]), "{n} year", "{n} years");
  const bust = /^after (\d+) bankruptc(?:y|ies)$/.exec(v);
  if (bust) return tp(Number(bust[1]), "after {n} bankruptcy", "after {n} bankruptcies");
  return txLine(v);
}
